import { EditorView } from "prosemirror-view";
import { Selection } from "prosemirror-state";
import { generateId } from "../../../model/generateId";
import { uploadAudioChunk } from "../../features/audioInsert/utils";
import { schema } from "../../schema";
import { topLevelTokenToProsersmirorNodes } from "../../bridge";
import { makeParagraph } from "../../../model/defaults";
import { AudioInsertToken } from "../../../../shared/types";
import { getEditorView } from "../../../model/atoms";
import logger from "../../../utils/logger";
import { Stage } from "../../features/audioInsert/audioTypes";
import {
  globalAudioRecordingCoordinator,
  updateAudioInsertTokens,
} from "../../features/audioInsert/audioRecordingCoordinator";

export function getAudioItems(dt: DataTransfer | null) {
  return Array.from(dt?.items ?? []).filter((item) => item.type.includes("audio"));
}

function getAudioDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith("audio/")) {
      return reject(new Error("Not an audio file"));
    }

    const audio = document.createElement("audio");
    audio.preload = "metadata";

    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(audio.src);
      resolve(audio.duration);
    };

    audio.onerror = () => {
      URL.revokeObjectURL(audio.src);
      reject(new Error("Failed to load audio metadata"));
    };

    audio.src = URL.createObjectURL(file);
  });
}

export async function handleRawAudioUpload(file: File, view: EditorView, insertPosition?: number): Promise<boolean> {
  const editorView = getEditorView();
  if (!editorView) throw new Error("Editor view is not available");
  const audioId = generateId();
  console.log("Uploaded");
  const audioInsertToken: AudioInsertToken = {
    type: "audioInsert",
    tokenId: generateId(),
    audioId,
    updatedAt: Date.now(),
    transcriptGenId: null,
    // Set to null to distinguish from legacy audio which has chunkCount = 0
    chunkCount: null,
    durations: null,
    transcript: null,
    isRevealed: true,
    transcriptLanguageOverride: null,
  };

  if (editorView.state.selection.$anchor.node(1)?.type !== schema.nodes.note) {
    logger.warn("Cannot insert audio at selection.");
    return false;
  }

  const duration = await getAudioDuration(file);

  const sel = editorView.state.selection;
  const tr = editorView.state.tr;
  const { nodes } = topLevelTokenToProsersmirorNodes([audioInsertToken, makeParagraph()]);
  tr.insert(sel.$anchor.pos, nodes);
  tr.setSelection(Selection.near(tr.doc.resolve(sel.$anchor.pos), -1));
  editorView.dispatch(tr);
  setTimeout(async () => {
    globalAudioRecordingCoordinator.setAudioInsertViewInternalStage(audioId, Stage.Saving);
    await uploadAudioChunk(audioId, 0, file);
    updateAudioInsertTokens(audioId, 0, duration);
    globalAudioRecordingCoordinator.setAudioInsertViewInternalStage(audioId, Stage.Viewing);
  }, 0);
  return true;
}
